import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faNewspaper,
  faBullhorn,
  faCreditCard,
  faExclamationTriangle,
  faShieldAlt,
  faPhone,
  faEnvelope,
  faGlobe,
  faCheckCircle,
  faStar,
  faHeart,
  faRocket,
  faEye,
  faLock,
  faFileContract,
  faGavel,
  faLink,
  faBan
} from '@fortawesome/free-solid-svg-icons';

const TermCondition = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const staggerItem = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 },
    transition: { duration: 0.5 }
  };

  const terms = [
    {
      id: 1,
      title: "Eligibility",
      icon: faUsers,
      color: "from-blue-500 to-blue-600",
      bgColor: "from-blue-50 to-blue-100",
      borderColor: "border-blue-200",
      items: [
        "Users must be at least 18 years of age to register as Reporters or Advertisers.",
        "Children may use the site for general browsing, but any misuse or prohibited activity is strictly forbidden.",
        "Users must provide accurate and complete information during registration."
      ]
    },
    {
      id: 2,
      title: "Services Provided",
      icon: faRocket,
      color: "from-green-500 to-green-600",
      bgColor: "from-green-50 to-green-100",
      borderColor: "border-green-200",
      items: [
        "For Reporters: Access to verified advertisements, earning opportunities, and news contribution features.",
        "For Advertisers: Ability to publish ads on our platform, subject to verification and approval by IINSAF.",
        "For Readers: Access to news, reports, and updates shared through our platform."
      ]
    },
    {
      id: 3,
      title: "User Responsibilities",
      icon: faShieldAlt,
      color: "from-purple-500 to-purple-600",
      bgColor: "from-purple-50 to-purple-100",
      borderColor: "border-purple-200",
      items: [
        "By using our platform, you agree that you will not:",
        "• Upload, post, or share fraudulent, misleading, obscene, or illegal content.",
        "• Engage in nudity, scams, spam, illegal online gaming, or unlawful activities.",
        "• Attempt to hack, damage, or disrupt our website or services.",
        "• Use another person's account without permission.",
        "Any violation of these terms may result in immediate suspension or termination of your account, along with legal action if required."
      ],
      warning: "Violations may result in immediate account suspension and legal action."
    },
    {
      id: 4,
      title: "Verification & Payments",
      icon: faCreditCard,
      color: "from-orange-500 to-orange-600",
      bgColor: "from-orange-50 to-orange-100",
      borderColor: "border-orange-200",
      items: [
        "All users may be required to undergo identity verification (KYC) using documents like Aadhaar or PAN.",
        "Payments from advertisers are processed securely through Razorpay Payment Gateway.",
        "Payouts to reporters or users are made via UPI ID or Bank Account details provided by the user.",
        "IINSAF is not responsible for delays or issues caused by third-party payment providers."
      ]
    },
    {
      id: 5,
      title: "Content Policy",
      icon: faFileContract,
      color: "from-indigo-500 to-indigo-600",
      bgColor: "from-indigo-50 to-indigo-100",
      borderColor: "border-indigo-200",
      items: [
        "Content shared on IINSAF must comply with Indian laws and must not violate copyright, privacy, or intellectual property rights.",
        "IINSAF reserves the right to review, approve, or remove any content, advertisement, or report that violates our policies."
      ]
    },
    {
      id: 6,
      title: "Third-Party Links & Services",
      icon: faLink,
      color: "from-cyan-500 to-cyan-600",
      bgColor: "from-cyan-50 to-cyan-100",
      borderColor: "border-cyan-200",
      items: [
        "Our website may display content or links from third-party platforms like Facebook, YouTube, Instagram etc.",
        "We are not responsible for the availability, accuracy, or policies of third-party services.",
        "Use of such services will be governed by their respective Terms & Privacy Policies."
      ]
    },
    {
      id: 7,
      title: "Limitation of Liability",
      icon: faExclamationTriangle,
      color: "from-red-500 to-red-600",
      bgColor: "from-red-50 to-red-100",
      borderColor: "border-red-200",
      items: [
        "IINSAF is a platform to connect Reporters, Advertisers, and Readers.",
        "We are not responsible for any fraud, misrepresentation, or illegal activity carried out by users.",
        "Any disputes or claims arising due to misuse will be the sole responsibility of the individual involved."
      ]
    },
    {
      id: 8,
      title: "Account Termination",
      icon: faBan,
      color: "from-gray-500 to-gray-600",
      bgColor: "from-gray-50 to-gray-100",
      borderColor: "border-gray-200",
      items: [
        "We reserve the right to suspend or Logout accounts if users:",
        "• Provide false information,",
        "• Violate our Terms,",
        "• Engage in fraud, scams, or prohibited content."
      ]
    },
    {
      id: 9,
      title: "Policy Updates",
      icon: faStar,
      color: "from-yellow-500 to-yellow-600",
      bgColor: "from-yellow-50 to-yellow-100",
      borderColor: "border-yellow-200",
      items: [
        "These Terms may be updated or changed at any time without prior notice.",
        "The updated Terms will always be available on our website."
      ]
    },
    {
      id: 10,
      title: "Governing Law",
      icon: faGavel,
      color: "from-emerald-500 to-emerald-600",
      bgColor: "from-emerald-50 to-emerald-100",
      borderColor: "border-emerald-200",
      items: [
        "These Terms are governed by the laws of India.",
        "Any disputes shall be subject to the jurisdiction of Indian courts."
      ]
    },
    {
      id: 11,
      title: "Contact Us",
      icon: faPhone,
      color: "from-pink-500 to-pink-600",
      bgColor: "from-pink-50 to-pink-100",
      borderColor: "border-pink-200",
      items: [
        "Phone: 9992396623",
        "Email: iinsafofficial@gmail.com",
        "Website: www.iinsaf.com"
      ]
    }
  ];

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-blue-50 pt-20 pb-8 px-2 sm:px-4">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.95 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-7xl mx-auto mb-12"
      >
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12 md:p-16 lg:p-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-8"
          >
            <div className="inline-block bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
              TERMS AND CONDITIONS
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent">
                Terms
              </span>{' '}
              – IINSAF
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-red-500 to-red-600 mx-auto rounded-full mb-6"></div>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Welcome to IINSAF. By accessing or using our platform, you agree to comply with the following Terms and Conditions.
              Please read them carefully before using our services.
            </p>
          </motion.div>
        </div>
      </motion.div>

      {/* Terms Section */}
      <div className="w-full max-w-7xl mx-auto">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="space-y-6"
        >
          {terms.map((term, index) => (
            <motion.div
              key={term.id}
              variants={staggerItem}
              className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden hover:shadow-2xl transition-all duration-300"
            >
              <div className={`p-6 sm:p-8 bg-gradient-to-r ${term.bgColor} border-b ${term.borderColor}`}>
                <div className="flex items-center mb-6">
                  <div className={`w-12 h-12 bg-gradient-to-r ${term.color} rounded-xl flex items-center justify-center mr-4 shadow-lg`}>
                    <FontAwesomeIcon icon={term.icon} className="text-white text-xl" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                    {term.id}. {term.title}
                  </h2>
                </div>

                <div className="space-y-3">
                  {term.items.map((item, itemIndex) => (
                    <motion.div
                      key={itemIndex}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: itemIndex * 0.1 }}
                      className="flex items-start"
                    >
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className={`text-${term.color.split('-')[1]}-500 mt-1 mr-3 flex-shrink-0`}
                      />
                      <span className="text-gray-700 leading-relaxed">{item}</span>
                    </motion.div>
                  ))}

                  {term.warning && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: term.items.length * 0.1 }}
                      className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl"
                    >
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mr-3" />
                        <span className="text-red-700 font-semibold">{term.warning}</span>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-16 bg-gradient-to-r from-red-500 to-red-600 rounded-2xl shadow-xl p-6 sm:p-8 text-white"
        >
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4 shadow-lg">
              <FontAwesomeIcon icon={faHeart} className="text-white text-xl" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold">Questions About Terms?</h2>
          </div>
          <p className="text-white/90 mb-6 text-lg">
            Need clarification on any terms or have legal questions? Our support team is here to help.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <FontAwesomeIcon icon={faPhone} className="text-white text-xl mr-4" />
              <div>
                <p className="font-semibold">Phone</p>
                <a href="tel:9992396623" className="text-white/90 hover:text-white transition-colors duration-300">
                  9992396623
                </a>
              </div>
            </div>
            <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <FontAwesomeIcon icon={faEnvelope} className="text-white text-xl mr-4" />
              <div>
                <p className="font-semibold">Email</p>
                <a href="mailto:iinsafofficial@gmail.com" className="text-white/90 hover:text-white transition-colors duration-300">
                  iinsafofficial@gmail.com
                </a>
              </div>
            </div>
            <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <FontAwesomeIcon icon={faGlobe} className="text-white text-xl mr-4" />
              <div>
                <p className="font-semibold">Website</p>
                <a href="https://www.iinsaf.com" className="text-white/90 hover:text-white transition-colors duration-300" target="_blank" rel="noopener noreferrer">
                  www.iinsaf.com
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermCondition;
